import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { TableKit } from "@tiptap/extension-table";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Heading2, Heading3, ImagePlus, Italic, Link2, List, ListOrdered, Quote, Redo2, Table2, Undo2, Unlink } from "lucide-react";
import { useId, useState } from "react";
import { Button, FormField, Input } from "./ui/controls";
import { Dialog } from "./ui/overlays";
import { MediaPicker } from "./media-picker";
import styles from "./editor/rich-text-editor.module.css";

function ToolButton({ label, active = false, disabled = false, onClick, children }: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" className={styles.tool} data-active={active} disabled={disabled} aria-label={label} title={label} onClick={onClick}>{children}</button>;
}

export function RichTextEditor({ id, name, defaultValue = "", onChange }: { id?: string; name: string; defaultValue?: string; onChange?: (value: string) => void }) {
  const generatedId = useId();
  const editorId = id ?? generatedId;
  const linkInputId = `${generatedId}-link`;
  const imageAltInputId = `${generatedId}-image-alt`;
  const [value, setValue] = useState(defaultValue);
  const [linkOpen, setLinkOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [linkError, setLinkError] = useState("");
  const [image, setImage] = useState<{ id: string | null; url: string }>({ id: null, url: "" });
  const [imageAlt, setImageAlt] = useState("");
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      TableKit.configure({ table: { resizable: false } }),
      Image.configure({ allowBase64: false, HTMLAttributes: { loading: "lazy" } }),
    ],
    content: defaultValue,
    editorProps: { attributes: { id: editorId, class: "rich-editor-content", "aria-label": "Detay içeriği" } },
    onUpdate: ({ editor: current }) => { const nextValue = current.getHTML(); setValue(nextValue); onChange?.(nextValue); },
  });

  function applyLink() {
    const href = linkValue.trim();
    if (!href) editor?.chain().focus().unsetLink().run();
    else if (!/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(href)) {
      setLinkError("HTTP(S), e-posta, telefon veya site içi bir bağlantı girin.");
      return;
    } else editor?.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkOpen(false);
    setLinkValue("");
    setLinkError("");
  }
  function insertImage() {
    if (!image.url) return;
    editor?.chain().focus().setImage({ src: image.url, alt: imageAlt.trim() }).run();
    setMediaOpen(false);
    setImage({ id: null, url: "" });
    setImageAlt("");
  }

  return <div className={`${styles.root} rich-editor`}>
    <input type="hidden" name={name} value={value} />
    <div className={`${styles.toolbar} rich-toolbar`} role="toolbar" aria-label="Metin biçimlendirme">
      <div className={styles.group}>
        <ToolButton label="Geri al" disabled={!editor?.can().chain().focus().undo().run()} onClick={() => { editor?.chain().focus().undo().run(); }}><Undo2 size={16} /></ToolButton>
        <ToolButton label="Yinele" disabled={!editor?.can().chain().focus().redo().run()} onClick={() => { editor?.chain().focus().redo().run(); }}><Redo2 size={16} /></ToolButton>
      </div>
      <div className={styles.group}>
        <ToolButton label="Kalın" active={Boolean(editor?.isActive("bold"))} onClick={() => { editor?.chain().focus().toggleBold().run(); }}><Bold size={16} /></ToolButton>
        <ToolButton label="İtalik" active={Boolean(editor?.isActive("italic"))} onClick={() => { editor?.chain().focus().toggleItalic().run(); }}><Italic size={16} /></ToolButton>
      </div>
      <div className={styles.group}>
        <ToolButton label="İkinci seviye başlık" active={Boolean(editor?.isActive("heading", { level: 2 }))} onClick={() => { editor?.chain().focus().toggleHeading({ level: 2 }).run(); }}><Heading2 size={16} /></ToolButton>
        <ToolButton label="Üçüncü seviye başlık" active={Boolean(editor?.isActive("heading", { level: 3 }))} onClick={() => { editor?.chain().focus().toggleHeading({ level: 3 }).run(); }}><Heading3 size={16} /></ToolButton>
        <ToolButton label="Madde listesi" active={Boolean(editor?.isActive("bulletList"))} onClick={() => { editor?.chain().focus().toggleBulletList().run(); }}><List size={16} /></ToolButton>
        <ToolButton label="Numaralı liste" active={Boolean(editor?.isActive("orderedList"))} onClick={() => { editor?.chain().focus().toggleOrderedList().run(); }}><ListOrdered size={16} /></ToolButton>
        <ToolButton label="Alıntı veya vurgu" active={Boolean(editor?.isActive("blockquote"))} onClick={() => { editor?.chain().focus().toggleBlockquote().run(); }}><Quote size={16} /></ToolButton>
      </div>
      <div className={styles.group}>
        <ToolButton label="Bağlantı ekle" active={Boolean(editor?.isActive("link"))} onClick={() => { setLinkValue(editor?.getAttributes("link").href ?? ""); setLinkError(""); setLinkOpen(true); }}><Link2 size={16} /></ToolButton>
        <ToolButton label="Bağlantıyı kaldır" disabled={!editor?.isActive("link")} onClick={() => { editor?.chain().focus().unsetLink().run(); }}><Unlink size={16} /></ToolButton>
        <ToolButton label="Tablo ekle" active={Boolean(editor?.isActive("table"))} onClick={() => { editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); }}><Table2 size={16} /></ToolButton>
        <ToolButton label="Medya ekle" onClick={() => setMediaOpen(true)}><ImagePlus size={16} /></ToolButton>
      </div>
    </div>
    <EditorContent editor={editor} />
    <div className={styles.footer}>{editor?.storage.characterCount?.characters?.() ?? editor?.getText().length ?? 0} karakter</div>
    <Dialog
      open={linkOpen}
      onOpenChange={setLinkOpen}
      title="Bağlantı ekle"
      description="Seçili metnin yönleneceği güvenli adresi girin."
      size="sm"
      footer={<><Button variant="secondary" onClick={() => setLinkOpen(false)}>Vazgeç</Button><Button onClick={applyLink}>Uygula</Button></>}
    >
      <FormField htmlFor={linkInputId} label="Bağlantı adresi" error={linkError || undefined}>
        <Input id={linkInputId} autoFocus type="url" value={linkValue} onChange={event => { setLinkValue(event.target.value); setLinkError(""); }} placeholder="https://" />
      </FormField>
    </Dialog>
    <Dialog
      open={mediaOpen}
      onOpenChange={setMediaOpen}
      title="İçeriğe görsel ekle"
      description="Medya kütüphanesinden bir görsel seçin ve açıklamasını yazın."
      size="lg"
      footer={<><Button variant="secondary" onClick={() => setMediaOpen(false)}>Vazgeç</Button><Button disabled={!image.url || !imageAlt.trim()} onClick={insertImage}>Görseli ekle</Button></>}
    >
      <div className={styles.mediaDialogBody}>
        <MediaPicker label="Görsel" assetId={image.id} imageUrl={image.url} onSelect={(id, url) => setImage({ id, url })} />
        <FormField htmlFor={imageAltInputId} label="Alternatif metin">
          <Input id={imageAltInputId} value={imageAlt} onChange={event => setImageAlt(event.target.value)} />
        </FormField>
      </div>
    </Dialog>
  </div>;
}
